<template>
  <div class="app">
    <!-- 添加加载动画 -->
    <div class="loading-screen" v-if="isLoading">
      <div class="loading-content">
        <div class="loading-logo">
          <i class="fas fa-feather-alt"></i>
        </div>
        <div class="loading-text">Quick AI Writer - ChatBot</div>
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
        </div>
      </div>
    </div>

    <header class="header">
      <nav class="nav-container">
        <div class="logo">
          <i class="fas fa-feather-alt"></i>
          Quick AI Writer - ChatBot
        </div>
        <div class="nav-links">
          <a href="#features">Features</a>
          <a href="#about">About</a>
          <a href="#download">Download</a>
        </div>
      </nav>
    </header>

    <main>
      <section class="hero">
        <div class="hero-content">
          <h1 class="animate-title">Quick AI Writer - ChatBot</h1>
          <p class="subtitle animate-subtitle">Your Intelligent Writing & Chat Assistant</p>
          <p class="description animate-description">
            Unleash your creative potential with AI-powered writing. Whether it's content creation, editing, or daily communication,
            Quick AI Writer is your reliable assistant. Experience effortless writing with advanced artificial intelligence technology.
          </p>
          <div class="highlights animate-highlights">
            <div class="highlight-item">
              <i class="fas fa-check-circle"></i>
              <span>Smart Writing Assistant</span>
            </div>
            <div class="highlight-item">
              <i class="fas fa-check-circle"></i>
              <span>Multi-Scenario Support</span>
            </div>
            <div class="highlight-item">
              <i class="fas fa-check-circle"></i>
              <span>Real-time Response</span>
            </div>
          </div>
          <div class="download-buttons animate-buttons" data-section="download">
            <a href="https://apps.apple.com/app/id6469693390" class="download-btn ios" target="_blank" rel="noopener noreferrer">
              <div class="btn-content">
                <i class="fab fa-apple"></i>
                <div class="btn-text">
                  <span class="small-text">Download</span>
                  <span class="large-text">iOS Version</span>
                </div>
              </div>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.smart.chat.yi.aichatyi" class="download-btn android" target="_blank" rel="noopener noreferrer">
              <div class="btn-content">
                <i class="fab fa-android"></i>
                <div class="btn-text">
                  <span class="small-text">Download</span>
                  <span class="large-text">Android Version</span>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      <section id="features" class="features" data-section="features">
        <h2 class="section-title">Powerful Features</h2>
        <div class="feature-grid">
          <div class="feature-card" v-for="(feature, index) in features" :key="index">
            <div class="feature-icon">
              <i :class="feature.icon"></i>
            </div>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </div>
        </div>
        <div class="feature-highlights">
          <div class="highlight-row">
            <div class="highlight-box">
              <i class="fas fa-rocket"></i>
              <h4>Lightning Fast</h4>
              <p>Millisecond response time, smooth operation experience</p>
            </div>
            <div class="highlight-box">
              <i class="fas fa-magic"></i>
              <h4>Smart Suggestions</h4>
              <p>Context-aware writing recommendations</p>
            </div>
            <div class="highlight-box">
              <i class="fas fa-palette"></i>
              <h4>Multiple Styles</h4>
              <p>Support for various writing styles</p>
            </div>
          </div>
        </div>
      </section>

      <section class="use-cases">
        <h2 class="section-title">Use Cases</h2>
        <div class="use-cases-container">
          <div class="use-case-item">
            <div class="use-case-content">
              <div class="use-case-icon">
                <i class="fas fa-pen-fancy"></i>
              </div>
              <div class="use-case-text">
                <h3>Content Creation</h3>
                <p>Easily create various types of content, from short posts to long articles, with AI assistance to overcome writer's block</p>
              </div>
            </div>
            <div class="use-case-bg"></div>
          </div>

          <div class="use-case-item reverse">
            <div class="use-case-content">
              <div class="use-case-icon">
                <i class="fas fa-envelope-open-text"></i>
              </div>
              <div class="use-case-text">
                <h3>Email Writing</h3>
                <p>Generate professional emails quickly for various scenarios, saving your valuable time</p>
              </div>
            </div>
            <div class="use-case-bg"></div>
          </div>

          <div class="use-case-item">
            <div class="use-case-content">
              <div class="use-case-icon">
                <i class="fas fa-comments"></i>
              </div>
              <div class="use-case-text">
                <h3>Social Communication</h3>
                <p>Intelligent conversation assistant for more natural and appropriate social interactions</p>
              </div>
            </div>
            <div class="use-case-bg"></div>
          </div>

          <div class="use-case-item reverse">
            <div class="use-case-content">
              <div class="use-case-icon">
                <i class="fas fa-book"></i>
              </div>
              <div class="use-case-text">
                <h3>Learning Support</h3>
                <p>Knowledge queries and homework assistance to make learning easier</p>
              </div>
            </div>
            <div class="use-case-bg"></div>
          </div>
        </div>
      </section>

      <section id="about" class="about" data-section="about">
        <h2 class="section-title">About Us</h2>
        <div class="about-content">
          <div class="about-text">
            <p class="about-description">The Quick AI Writer team is dedicated to providing the best AI writing experience. Our mission is to empower everyone to create quality content effortlessly. Leveraging advanced AI technology, we continuously innovate to bring you smarter and more efficient writing solutions.</p>
            <div class="about-highlights">
              <div class="about-highlight-item">
                <i class="fas fa-users"></i>
                <h4>Expert Team</h4>
                <p>AI experts and writing professionals working together</p>
              </div>
              <div class="about-highlight-item">
                <i class="fas fa-chart-line"></i>
                <h4>Continuous Innovation</h4>
                <p>Ongoing algorithm optimization and experience enhancement</p>
              </div>
              <div class="about-highlight-item">
                <i class="fas fa-heart"></i>
                <h4>User-Centric</h4>
                <p>Focused on user needs with dedicated support</p>
              </div>
            </div>
            <div class="stats">
              <div class="stat-item">
                <span class="stat-number">1M+</span>
                <span class="stat-label">Active Users</span>
                <span class="stat-description">Trusted Choice</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">50+</span>
                <span class="stat-label">Languages</span>
                <span class="stat-description">Global Access</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">4.9</span>
                <span class="stat-label">App Rating</span>
                <span class="stat-description">User Satisfaction</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <footer class="footer">
      <div class="footer-content">
        <div class="footer-section">
          <h3>Quick AI Writer - ChatBot</h3>
          <p>The New Era of Smart Writing</p>
          <div class="social-links">
            <a href="#" title="Discord"><i class="fab fa-discord"></i></a>
            <a href="#" title="Twitter"><i class="fab fa-twitter"></i></a>
            <a href="#" title="Github"><i class="fab fa-github"></i></a>
          </div>
        </div>
        <div class="footer-section">
          <h4>Quick Links</h4>
          <a href="#features">Features</a>
          <a href="#about">About</a>
          <a href="#download">Download</a>
        </div>
        <div class="footer-section">
          <h4>Help & Support</h4>
          <a href="#">Tutorials</a>
          <a href="#">FAQ</a>
          <a href="#">Terms of Service</a>
          <a href="#">Privacy Policy</a>
        </div>
        <div class="footer-section">
          <h4>Contact Us</h4>
          <p class="contact-info">Working Hours: Mon-Fri 9:00-18:00</p>
          <div class="social-links">
            <a href="#" title="Discord"><i class="fab fa-discord"></i></a>
            <a href="#" title="Twitter"><i class="fab fa-twitter"></i></a>
            <a href="#" title="Github"><i class="fab fa-github"></i></a>
          </div>
        </div>
      </div>
      <div class="footer-bottom">
        <p>&copy; 2024 Quick AI Writer - ChatBot. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      isLoading: true,
      features: [
        {
          icon: 'fas fa-brain',
          title: 'Smart Writing',
          description: 'Powerful AI-driven assistance to help you create high-quality content, supporting multiple writing styles and scenarios'
        },
        {
          icon: 'fas fa-comments',
          title: 'Intelligent Chat',
          description: 'Natural and fluid conversation experience, providing professional advice anytime, anywhere'
        },
        {
          icon: 'fas fa-bolt',
          title: 'Quick Response',
          description: 'Millisecond-level response speed for smooth creation, never interrupting your inspiration'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Secure & Reliable',
          description: 'Strict data protection mechanisms ensuring your privacy and security'
        },
        {
          icon: 'fas fa-language',
          title: 'Multi-Language',
          description: 'Support for writing and translation in over 50 languages, breaking down language barriers'
        },
        {
          icon: 'fas fa-cloud-upload-alt',
          title: 'Cloud Sync',
          description: 'Automatic cloud backup and synchronization for seamless multi-device usage'
        }
      ]
    }
  },
  mounted() {
    // 设置页面标题
    document.title = 'Quick AI Writer - ChatBot';
    
    // 模拟加载时间
    setTimeout(() => {
      this.isLoading = false;
    }, 2000);
    this.initScrollAnimations();
    this.initSmoothScroll();
  },
  methods: {
    initScrollAnimations() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      }, {
        threshold: 0.1
      });

      document.querySelectorAll('.feature-card, .screenshot, .stat-item, .footer-section').forEach(el => {
        observer.observe(el);
        el.classList.add('animate-on-scroll');
      });
    },
    initSmoothScroll() {
      document.querySelectorAll('.nav-links a, .footer-section a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
          e.preventDefault();
          const targetId = anchor.getAttribute('href').replace('#', '');
          const targetElement = document.querySelector(`[data-section="${targetId}"]`) || document.querySelector(`#${targetId}`);
          if (targetElement) {
            const headerOffset = 70;
            const elementPosition = targetElement.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        });
      });
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css');

:root {
  --primary-color: #2563eb;
  --primary-dark: #1d4ed8;
  --text-color: #1e293b;
  --text-light: #64748b;
  --background-light: #f8fafc;
  --white: #ffffff;
  --shadow-sm: 0 2px 4px rgba(0,0,0,0.05);
  --shadow-md: 0 4px 6px rgba(0,0,0,0.1);
  --shadow-lg: 0 10px 15px rgba(0,0,0,0.1);
  scroll-behavior: smooth;
  scroll-padding-top: 80px;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', sans-serif;
  line-height: 1.6;
  color: var(--text-color);
  overflow-x: hidden;
}

.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Header Styles */
.header {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  height: 70px;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: fadeInLeft 1s ease;
}

.logo i {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.nav-links a {
  margin-left: 0;
  text-decoration: none;
  color: var(--text-color);
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  animation: fadeInRight 1s ease;
  animation-fill-mode: both;
  padding: 0.5rem 0;
}

.nav-links a::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, var(--primary-color), var(--primary-dark));
  transform: scaleX(0);
  transition: transform 0.3s ease;
  transform-origin: center;
}

.nav-links a:hover {
  color: var(--primary-color);
}

.nav-links a:hover::after {
  transform: scaleX(1);
}

.nav-links a:nth-child(1) { animation-delay: 0.1s; }
.nav-links a:nth-child(2) { animation-delay: 0.2s; }
.nav-links a:nth-child(3) { animation-delay: 0.3s; }
.nav-links a:nth-child(4) { animation-delay: 0.4s; }

/* Hero Section */
.hero {
  background: linear-gradient(135deg, #4f8fff 0%, #2b6cb0 100%);
  color: var(--white);
  padding: 8rem 2rem 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  text-align: center;
}

.hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.1;
  animation: patternFloat 30s linear infinite;
}

.hero::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.hero-content {
  max-width: 800px;
  position: relative;
  z-index: 1;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: fadeInUp 1s ease;
}

.hero h1 {
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(to right, #ffffff, #e2e8f0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.hero .subtitle {
  font-size: 1.5rem;
  margin-bottom: 2rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.hero .description {
  font-size: 1.1rem;
  line-height: 1.8;
  max-width: 600px;
  margin-bottom: 2rem;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.highlights {
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  justify-content: center;
}

.highlight-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.highlight-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.highlight-item i {
  color: #4ade80;
  font-size: 1.1rem;
}

.highlight-item span {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

/* Download Buttons */
.download-buttons {
  display: flex;
  gap: 2rem;
  margin-top: 3rem;
  justify-content: center;
  width: 100%;
  animation: fadeInUp 1s ease 0.6s backwards;
  scroll-margin-top: 80px;
}

.download-btn {
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem 1.5rem;
  border-radius: 16px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  width: 200px;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(12px);
}

.download-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    120deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transform: translateX(-100%);
  transition: 0.6s ease;
}

.download-btn::after {
  content: '';
  position: absolute;
  inset: 1px;
  border-radius: 15px;
  background: linear-gradient(
    rgba(255, 255, 255, 0.15),
    rgba(255, 255, 255, 0.05)
  );
  opacity: 0;
  transition: 0.3s ease;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: flex-start;
  width: 100%;
  position: relative;
  z-index: 1;
  padding: 0 0.3rem;
}

.btn-content i {
  font-size: 1.8rem;
  color: var(--white);
  transition: all 0.4s ease;
  width: 32px;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
}

.btn-content i::after {
  content: '';
  position: absolute;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) scale(0);
  transition: 0.3s ease;
  z-index: -1;
}

.btn-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  transform: translateX(0);
  transition: 0.4s ease;
}

.small-text {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.large-text {
  font-size: 1.1rem;
  color: var(--white);
  font-weight: 600;
  letter-spacing: 0.01em;
  margin-top: 1px;
}

.download-btn:hover {
  transform: translateY(-2px) scale(1.02);
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2),
              0 2px 8px rgba(255, 255, 255, 0.1),
              inset 0 0 0 1px rgba(255, 255, 255, 0.2);
}

.download-btn:hover::before {
  transform: translateX(100%);
}

.download-btn:hover::after {
  opacity: 1;
}

.download-btn:hover .btn-content i {
  transform: scale(1.2) rotate(-10deg);
  color: rgba(255, 255, 255, 1);
}

.download-btn:hover .btn-content i::after {
  transform: translate(-50%, -50%) scale(1);
}

.download-btn:hover .btn-text {
  transform: translateX(5px);
}

.download-btn:hover .small-text {
  color: rgba(255, 255, 255, 0.95);
}

.download-btn:hover .large-text {
  color: rgba(255, 255, 255, 1);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.download-btn:active {
  transform: translateY(0) scale(0.98);
}

@media (max-width: 768px) {
  .download-buttons {
    flex-direction: column;
    align-items: center;
    gap: 1.2rem;
    margin-top: 2.5rem;
  }

  .download-btn {
    width: 240px;
    padding: 1.2rem 1.8rem;
  }

  .btn-content i {
    font-size: 2rem;
    width: 36px;
  }

  .small-text {
    font-size: 0.85rem;
  }

  .large-text {
    font-size: 1.2rem;
  }
}

@media (max-width: 480px) {
  .download-buttons {
    margin-top: 2rem;
  }

  .download-btn {
    width: 220px;
    padding: 1rem 1.5rem;
  }

  .btn-content i {
    font-size: 1.8rem;
    width: 32px;
  }

  .small-text {
    font-size: 0.8rem;
  }

  .large-text {
    font-size: 1.1rem;
  }
}

/* Features Section */
.features {
  padding: 8rem 2rem;
  background-color: var(--background-light);
  margin-bottom: 0;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 4rem;
  color: var(--text-color);
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -1rem;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: linear-gradient(to right, var(--primary-color), var(--primary-dark));
  border-radius: 2px;
}

.feature-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  padding: 0 1rem;
}

.feature-card {
  background: var(--white);
  padding: 2.5rem;
  border-radius: 16px;
  text-align: center;
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: scaleIn 0.6s ease-out;
  animation-fill-mode: both;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, var(--primary-color), var(--primary-dark));
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.feature-card:hover::before {
  transform: scaleX(1);
}

.feature-card:hover {
  transform: translateY(-10px) scale(1.02);
}

.feature-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: float 4s ease-in-out infinite;
}

.feature-icon i {
  font-size: 2rem;
  color: var(--white);
}

.feature-card h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--text-color);
}

.feature-card p {
  color: var(--text-light);
  line-height: 1.8;
}

/* About Section */
.about {
  padding: 8rem 2rem;
  background-color: var(--background-light);
}

.about-content {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.about-text {
  max-width: 800px;
  margin: 0 auto;
  font-size: 1.2rem;
  line-height: 1.8;
  color: var(--text-light);
}

.stats {
  display: flex;
  justify-content: center;
  gap: 4rem;
  margin-top: 4rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: fadeInUp 0.6s ease-out;
  animation-fill-mode: both;
}

.stat-item:nth-child(1) { animation-delay: 0.1s; }
.stat-item:nth-child(2) { animation-delay: 0.2s; }
.stat-item:nth-child(3) { animation-delay: 0.3s; }

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.stat-item:hover .stat-number {
  color: var(--primary-dark);
  transform: scale(1.1);
}

.stat-label {
  color: var(--text-light);
  font-size: 1.1rem;
}

/* Footer */
.footer {
  background-color: var(--text-color);
  color: var(--white);
  padding: 4rem 2rem 2rem;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.5fr;
  gap: 3rem;
  margin-bottom: 3rem;
}

.footer-section {
  animation: fadeInUp 0.6s ease-out;
  animation-fill-mode: both;
}

.footer-section:nth-child(1) { animation-delay: 0.1s; }
.footer-section:nth-child(2) { animation-delay: 0.2s; }
.footer-section:nth-child(3) { animation-delay: 0.3s; }
.footer-section:nth-child(4) { animation-delay: 0.4s; }

.footer-section h3 {
  font-size: 1.5rem;
  margin-bottom: 1.2rem;
  color: rgba(255, 255, 255, 0.95);
}

.footer-section h4 {
  font-size: 1.2rem;
  margin-bottom: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
}

.footer-section p {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1rem;
  font-size: 0.95rem;
}

.footer-section a {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  display: block;
  margin-bottom: 0.8rem;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  position: relative;
  padding-left: 0;
}

.footer-section a:hover {
  color: var(--white);
  transform: translateX(8px);
}

.social-links {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.social-links a {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin: 0;
  padding: 0;
  position: relative;
  overflow: hidden;
}

.social-links a::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.social-links a:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-3px);
}

.social-links a:hover::before {
  opacity: 1;
}

.social-links i {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
}

.social-links a:hover i {
  transform: scale(1.1);
  color: rgba(255, 255, 255, 1);
}

.contact-info {
  color: rgba(255, 255, 255, 0.6);
  margin-top: 0.8rem;
  font-size: 0.9rem;
  line-height: 1.6;
}

.footer-bottom {
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
}

@media (max-width: 1024px) {
  .footer-content {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }
}

@media (max-width: 768px) {
  .footer {
    padding: 3.5rem 1.5rem 1.5rem;
  }

  .footer-content {
    grid-template-columns: 1fr;
    gap: 2.5rem;
    text-align: center;
    padding: 0;
  }

  .footer-section {
    padding: 0 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .social-links {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1.2rem;
    margin-top: 1.2rem;
  }

  .social-links a {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
    transform: none;
  }

  .social-links a:hover {
    transform: translateY(-2px);
  }

  .social-links i {
    font-size: 1.2rem;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .footer-section h3 {
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }

  .footer-section h4 {
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .footer-section a {
    margin-bottom: 0.7rem;
    font-size: 0.9rem;
    display: inline-block;
  }

  .footer-section a:hover {
    transform: translateY(-2px);
  }

  .contact-info {
    margin-top: 0.6rem;
    font-size: 0.85rem;
  }

  .footer-bottom {
    margin-top: 2rem;
    padding-top: 1.5rem;
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .footer {
    padding: 3rem 1rem 1rem;
  }

  .footer-content {
    gap: 2rem;
  }

  .footer-section {
    padding: 0 0.5rem;
  }

  .footer-section h3 {
    font-size: 1.3rem;
  }

  .footer-section h4 {
    font-size: 1rem;
  }

  .footer-section a {
    font-size: 0.85rem;
    margin-bottom: 0.6rem;
  }

  .social-links {
    gap: 1rem;
  }

  .social-links a {
    width: 36px;
    height: 36px;
  }

  .social-links i {
    font-size: 1.1rem;
  }

  .footer-bottom {
    margin-top: 1.5rem;
    padding-top: 1.2rem;
    font-size: 0.8rem;
  }
}

/* Animations */
@keyframes scroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
}

.animate-title {
  animation: fadeInUp 1s ease;
}

.animate-subtitle {
  animation: fadeInUp 1s ease 0.2s backwards;
}

.animate-description {
  animation: fadeInUp 1s ease 0.4s backwards;
}

.animate-buttons {
  animation: fadeInUp 1s ease 0.6s backwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
}

@keyframes patternFloat {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 400px 400px;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero {
    padding: 5rem 1.2rem 3rem;
    min-height: 85vh;
    display: flex;
    align-items: center;
  }

  .hero h1 {
    font-size: 2.2rem;
    margin-bottom: 1rem;
    line-height: 1.2;
  }

  .hero .subtitle {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }

  .hero .description {
    font-size: 0.95rem;
    line-height: 1.7;
    margin-bottom: 1.5rem;
    padding: 0 0.5rem;
  }

  .highlights {
    gap: 0.8rem;
    padding: 0 0.5rem;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 300px;
    margin: 0 auto 2rem;
  }

  .highlight-item {
    padding: 0.4rem 0.8rem;
    border-radius: 15px;
    width: 100%;
    justify-content: center;
    text-align: center;
  }

  .highlight-item i {
    font-size: 0.9rem;
  }

  .highlight-item span {
    font-size: 0.8rem;
    white-space: nowrap;
  }

  .nav-container {
    padding: 0.8rem 1.2rem;
  }

  .logo {
    font-size: 1.2rem;
  }

  .nav-links {
    display: none;
  }

  .feature-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0 1rem;
  }

  .feature-card {
    padding: 2rem 1.5rem;
    margin: 0 0.5rem;
  }

  .feature-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 1.2rem;
  }

  .feature-icon i {
    font-size: 1.5rem;
  }

  .feature-card h3 {
    font-size: 1.3rem;
    margin-bottom: 0.8rem;
  }

  .feature-card p {
    font-size: 0.95rem;
    line-height: 1.6;
  }

  .section-title {
    font-size: 2rem;
    margin-bottom: 3rem;
  }

  .use-cases {
    padding: 4rem 1.2rem;
  }

  .use-cases-container {
    padding: 2rem 0;
  }

  .use-case-item {
    margin: 2rem 0;
    padding: 0 1rem;
  }

  .use-case-content {
    flex-direction: column;
    text-align: center;
    padding: 2rem;
    gap: 1.5rem;
  }

  .use-case-bg {
    left: 2rem;
    right: 2rem;
  }

  .use-case-icon {
    width: 70px;
    height: 70px;
  }

  .use-case-icon i {
    font-size: 1.8rem;
  }

  .use-case-text h3 {
    font-size: 1.4rem;
  }

  .use-case-text p {
    font-size: 1rem;
    margin: 0 auto;
  }
}

@media (max-width: 480px) {
  .hero h1 {
    font-size: 2rem;
  }

  .hero .subtitle {
    font-size: 1rem;
  }

  .highlight-item {
    width: 100%;
  }

  .download-buttons {
    flex-direction: column;
    align-items: center;
  }

  .download-btn {
    width: 180px;
  }

  .stats {
    flex-direction: column;
    gap: 1.5rem;
  }

  .footer-content {
    grid-template-columns: 1fr;
    text-align: center;
  }
}

/* 添加平滑过渡效果 */
.hero-content,
.feature-card,
.use-case-card,
.about-highlight-item,
.stat-item,
.footer-section {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

/* 优化移动端动画效果 */
@media (max-width: 768px) {
  .animate-on-scroll {
    transform: translateY(15px);
  }

  .animate-in {
    transform: translateY(0);
  }

  .feature-card:hover,
  .use-case-card:hover,
  .about-highlight-item:hover {
    transform: translateY(-5px);
  }
}

.feature-highlights {
  margin-top: 5rem;
  padding: 0 2rem;
}

.highlight-row {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.highlight-box {
  background: var(--white);
  padding: 2.5rem 2rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
}

.highlight-box::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(37, 99, 235, 0.1) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.highlight-box:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-lg);
}

.highlight-box:hover::before {
  opacity: 1;
}

.highlight-box i {
  font-size: 2.5rem;
  color: var(--primary-color);
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.highlight-box:hover i {
  transform: scale(1.1) rotate(-5deg);
}

.highlight-box h4 {
  font-size: 1.5rem;
  color: var(--text-color);
  margin-bottom: 1rem;
  font-weight: 600;
}

.highlight-box p {
  color: var(--text-light);
  font-size: 1rem;
  line-height: 1.6;
  max-width: 280px;
  margin: 0 auto;
}

@media (max-width: 1024px) {
  .highlight-row {
    grid-template-columns: repeat(2, 1fr);
    padding: 0 1rem;
  }

  .highlight-box {
    min-height: 220px;
    padding: 2rem 1.5rem;
  }
}

@media (max-width: 768px) {
  .feature-highlights {
    margin-top: 4rem;
    padding: 0 1rem;
  }

  .highlight-row {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .highlight-box {
    min-height: auto;
    padding: 2rem 1.5rem;
  }

  .highlight-box i {
    font-size: 2.2rem;
    margin-bottom: 1.2rem;
  }

  .highlight-box h4 {
    font-size: 1.3rem;
    margin-bottom: 0.8rem;
  }

  .highlight-box p {
    font-size: 0.95rem;
    padding: 0 1rem;
  }
}

@media (max-width: 480px) {
  .feature-highlights {
    margin-top: 3rem;
  }

  .highlight-box {
    padding: 1.8rem 1.2rem;
  }

  .highlight-box i {
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  .highlight-box h4 {
    font-size: 1.2rem;
  }

  .highlight-box p {
    font-size: 0.9rem;
    padding: 0;
  }
}

.use-cases {
  padding: 8rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  position: relative;
  overflow: hidden;
}

.use-cases::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563eb' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.5;
}

.use-cases-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 0;
}

.use-case-item {
  position: relative;
  margin: 4rem 0;
  padding: 0 2rem;
  opacity: 0;
  transform: translateX(-50px);
  animation: slideIn 0.6s ease forwards;
}

.use-case-item.reverse {
  text-align: right;
  transform: translateX(50px);
}

.use-case-item:nth-child(1) { animation-delay: 0.1s; }
.use-case-item:nth-child(2) { animation-delay: 0.3s; }
.use-case-item:nth-child(3) { animation-delay: 0.5s; }
.use-case-item:nth-child(4) { animation-delay: 0.7s; }

.use-case-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 3rem;
  background: var(--white);
  border-radius: 20px;
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
}

.use-case-item.reverse .use-case-content {
  flex-direction: row-reverse;
}

.use-case-bg {
  position: absolute;
  top: 1rem;
  left: 3rem;
  right: 3rem;
  bottom: -1rem;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  border-radius: 20px;
  opacity: 0.1;
  transform: scale(0.95);
  transition: all 0.3s ease;
}

.use-case-item:hover .use-case-content {
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}

.use-case-item:hover .use-case-bg {
  transform: scale(1);
  opacity: 0.15;
}

.use-case-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.use-case-icon::before {
  content: '';
  position: absolute;
  inset: 1px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
  border-radius: 19px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.use-case-item:hover .use-case-icon::before {
  opacity: 1;
}

.use-case-icon i {
  font-size: 2rem;
  color: var(--white);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.use-case-item:hover .use-case-icon i {
  transform: scale(1.1) rotate(-5deg);
}

.use-case-text {
  flex: 1;
}

.use-case-text h3 {
  font-size: 1.6rem;
  color: var(--text-color);
  margin-bottom: 0.8rem;
  font-weight: 600;
}

.use-case-text p {
  color: var(--text-light);
  font-size: 1.1rem;
  line-height: 1.7;
  max-width: 500px;
}

.use-case-item.reverse .use-case-text p {
  margin-left: auto;
}

@keyframes slideIn {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 768px) {
  .use-cases {
    padding: 5rem 1rem;
  }

  .use-case-item {
    margin: 2rem 0;
    padding: 0 1rem;
  }

  .use-case-content {
    flex-direction: column;
    text-align: center;
    padding: 2rem;
    gap: 1.5rem;
  }

  .use-case-item.reverse .use-case-content {
    flex-direction: column;
    text-align: center;
  }

  .use-case-bg {
    left: 2rem;
    right: 2rem;
  }

  .use-case-icon {
    width: 70px;
    height: 70px;
  }

  .use-case-icon i {
    font-size: 1.8rem;
  }

  .use-case-text h3 {
    font-size: 1.4rem;
  }

  .use-case-text p {
    font-size: 1rem;
    margin: 0 auto;
  }
}

@media (max-width: 480px) {
  .use-cases {
    padding: 4rem 0.5rem;
  }

  .use-case-item {
    margin: 1.5rem 0;
    padding: 0 0.5rem;
  }

  .use-case-content {
    padding: 1.5rem;
  }

  .use-case-bg {
    left: 1rem;
    right: 1rem;
  }

  .use-case-icon {
    width: 60px;
    height: 60px;
  }

  .use-case-icon i {
    font-size: 1.6rem;
  }

  .use-case-text h3 {
    font-size: 1.3rem;
  }

  .use-case-text p {
    font-size: 0.95rem;
  }
}

/* 移除CTA相关样式 */
.cta {
  display: none;
}

.cta-content {
  display: none;
}

.features, .about {
  scroll-margin-top: 70px;
}

/* Loading Screen Styles */
.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #4f8fff 0%, #2b6cb0 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.5s ease;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.loading-logo {
  font-size: 3rem;
  color: var(--white);
  animation: pulse 2s infinite;
}

.loading-logo i {
  background: linear-gradient(135deg, #ffffff, #e2e8f0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.loading-text {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--white);
  letter-spacing: 0.05em;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  animation: fadeInUp 0.5s ease 0.2s backwards;
}

.loading-spinner {
  position: relative;
  width: 60px;
  height: 60px;
  animation: fadeInUp 0.5s ease 0.4s backwards;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid var(--white);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner-ring::before {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), transparent);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: xor;
  -webkit-mask-composite: xor;
  animation: shimmer 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

@keyframes shimmer {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 当加载完成时，淡出加载界面 */
.loading-screen.fade-out {
  animation: fadeOut 0.5s ease forwards;
}

@keyframes fadeOut {
  from {
    opacity: 1;
    visibility: visible;
  }
  to {
    opacity: 0;
    visibility: hidden;
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .loading-logo {
    font-size: 2.5rem;
  }

  .loading-text {
    font-size: 1.2rem;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
  }
}
</style>


